/**
 * Settlement Policy Engine
 * 
 * Maps settlement status to allowed/blocked financial actions.
 * Consumed by JobFinancialsPanel and any UI that controls payment interactions.
 * 
 * This is additive — does NOT modify invoice engine or payment plans.
 */

import type { SettlementStatus, InstallerWrapRecord } from "./installerWrapEngine";

// ── Action Policy ──

export interface SettlementPolicy {
  /** Settlement status driving this policy */
  status: SettlementStatus;
  /** Whether payment collection is allowed */
  canCollectPayment: boolean;
  /** Whether invoices can be sent */
  canSendInvoice: boolean;
  /** Whether final invoice can be generated */
  canGenerateFinalInvoice: boolean;
  /** Whether the "Generate Invoice Plan" action is allowed */
  canGeneratePlan: boolean;
  /** Human-readable reason for any restrictions */
  restriction?: string;
  /** UI display severity */
  severity: "none" | "info" | "warning" | "block";
}

const POLICY_MAP: Record<SettlementStatus, SettlementPolicy> = {
  due_now: {
    status: "due_now",
    canCollectPayment: true,
    canSendInvoice: true,
    canGenerateFinalInvoice: true,
    canGeneratePlan: true,
    severity: "none",
  },
  deferred_until_completion: {
    status: "deferred_until_completion",
    canCollectPayment: false,
    canSendInvoice: true,
    canGenerateFinalInvoice: false,
    canGeneratePlan: true,
    restriction: "Payment deferred until install completion",
    severity: "info",
  },
  continuation_required: {
    status: "continuation_required",
    canCollectPayment: false,
    canSendInvoice: true,
    canGenerateFinalInvoice: false,
    canGeneratePlan: true,
    restriction: "Install incomplete — final payment suppressed",
    severity: "warning",
  },
  hold_issue: {
    status: "hold_issue",
    canCollectPayment: false,
    canSendInvoice: false,
    canGenerateFinalInvoice: false,
    canGeneratePlan: true,
    restriction: "Payment actions blocked — issue must be resolved",
    severity: "block",
  },
  collected: {
    status: "collected",
    canCollectPayment: false,
    canSendInvoice: false,
    canGenerateFinalInvoice: false,
    canGeneratePlan: false,
    restriction: "All payments collected",
    severity: "none",
  },
};

// ── Resolve Policy ──

/**
 * Get the active settlement policy for a job.
 * Uses the most recent wrap record's settlement status.
 * Falls back to permissive defaults if no wrap exists.
 */
export function getSettlementPolicy(
  jobId: string,
  wrapRecords: InstallerWrapRecord[],
): SettlementPolicy {
  // Find the most recent wrap for this job
  const jobWraps = wrapRecords
    .filter(r => r.jobId === jobId)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  if (jobWraps.length === 0) {
    // No wrap exists — default permissive (pre-install state)
    return POLICY_MAP.due_now;
  }

  return POLICY_MAP[jobWraps[0].settlementStatus];
}

/**
 * Get the raw policy for a given settlement status (for preview/display).
 */
export function getPolicyForStatus(status: SettlementStatus): SettlementPolicy {
  return POLICY_MAP[status];
}
